import { useEffect, useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { api } from "../api/client";
import { InstagramAccount } from "../api/types";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";
import Avatar from "../components/ui/Avatar";
import { Skeleton, SkeletonTiles } from "../components/ui/Skeleton";
import {
  IconTarget,
  IconAccounts,
  IconSearch,
  IconLock,
  IconUsers,
} from "../components/ui/Icons";

type CandidateStatus = "available" | "cooldown" | "processed" | "follower";

interface TargetsResponse {
  summary: {
    total: number;
    available: number;
    cooldown: number;
    processed: number;
    followers: number;
    following: { active: number; mutual: number };
  };
  campaigns: { id: string; name: string; enabled: boolean }[];
  candidates: {
    page: number;
    pageSize: number;
    total: number;
    rows: Candidate[];
  };
}

interface Candidate {
  id: string;
  username: string;
  fullName: string;
  source: "account_followers" | "hashtag" | "manual";
  sourceValue: string;
  campaignId: string | null;
  status: CandidateStatus;
  attempts: number;
  successfulActions: number;
  lastAttemptAt: string | null;
  nextEligibleAt: string | null;
  addedAt: string;
  followerCount: number | null;
  followingCount: number | null;
  isPrivate: boolean | null;
}

const STATUS_FILTERS: Array<"" | CandidateStatus> = [
  "",
  "available",
  "cooldown",
  "processed",
  "follower",
];

export default function Discover(): JSX.Element {
  const { t } = useTranslation();
  const accountsQuery = useQuery({
    queryKey: ["instagram-accounts"],
    queryFn: async () => {
      const { data } = await api.get<{ accounts: InstagramAccount[] }>(
        "/api/instagram-accounts"
      );
      return data.accounts;
    },
  });

  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [status, setStatus] = useState<"" | CandidateStatus>("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);

  const accounts = accountsQuery.data || [];

  useEffect(() => {
    if (!selectedAccountId && accounts.length) setSelectedAccountId(accounts[0].id);
  }, [accounts, selectedAccountId]);

  // Debounce the search box (300ms) so we don't fire a query per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Reset to page 1 whenever the filters change.
  useEffect(() => {
    setPage(1);
  }, [selectedAccountId, status, debouncedSearch]);

  const targetsQuery = useQuery({
    queryKey: ["targets", selectedAccountId, status, debouncedSearch, page],
    queryFn: async () => {
      const { data } = await api.get<TargetsResponse>(
        `/api/instagram-accounts/${selectedAccountId}/targets`,
        { params: { status, search: debouncedSearch, page, pageSize: 25 } }
      );
      return data;
    },
    enabled: !!selectedAccountId,
    placeholderData: keepPreviousData,
  });

  const data = targetsQuery.data;
  const pages = data
    ? Math.max(1, Math.ceil(data.candidates.total / data.candidates.pageSize))
    : 1;

  if (accountsQuery.isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow={t("discover.eyebrow")}
          title={t("discover.title")}
          subtitle={t("discover.subtitle")}
          icon={<IconTarget />}
        />
        <SkeletonTiles count={6} />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow={t("discover.eyebrow")}
          title={t("discover.title")}
          subtitle={t("discover.subtitle")}
          icon={<IconTarget />}
        />
        <EmptyState
          icon={<IconAccounts size={26} />}
          title={t("discover.empty.title")}
          description={t("discover.empty.subtitle")}
          action={
            <Link to="/campaigns" className="btn-primary px-5 py-2.5">
              {t("nav.campaigns")}
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("discover.eyebrow")}
        title={t("discover.title")}
        subtitle={t("discover.subtitle")}
        icon={<IconTarget />}
        actions={
          accounts.length > 1 ? (
            <label className="flex w-full items-center gap-2 sm:w-auto">
              <span className="sr-only">{t("discover.selectAccount")}</span>
              <select
                className="input sm:w-56"
                value={selectedAccountId || ""}
                onChange={(e) => setSelectedAccountId(e.target.value)}
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    @{a.igUsername}
                  </option>
                ))}
              </select>
            </label>
          ) : undefined
        }
      />

      {/* Summary counters */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-6">
        <SummaryTile
          label={t("discover.summary.scraped")}
          value={data?.summary.total}
          tone="slate"
        />
        <SummaryTile
          label={t("discover.summary.available")}
          value={data?.summary.available}
          tone="emerald"
        />
        <SummaryTile
          label={t("discover.summary.cooldown")}
          value={data?.summary.cooldown}
          tone="amber"
        />
        <SummaryTile
          label={t("discover.summary.processed")}
          value={data?.summary.processed}
          tone="violet"
        />
        <SummaryTile
          label={t("discover.summary.followers")}
          value={data?.summary.followers}
          tone="sky"
        />
        <SummaryTile
          label={t("discover.summary.mutual")}
          value={data?.summary.following.mutual}
          tone="rose"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s || "all"}
              type="button"
              onClick={() => setStatus(s)}
              className={clsx(
                "rounded-full px-3 py-1.5 text-xs font-medium transition",
                status === s
                  ? "bg-brand-500/20 text-brand-100 ring-1 ring-brand-400/30"
                  : "bg-slate-900/60 text-slate-300 ring-1 ring-slate-800 hover:text-white"
              )}
            >
              {s === "" ? t("discover.filter.all") : t(`discover.filter.${s}`)}
            </button>
          ))}
        </div>
        <label className="relative w-full sm:w-64">
          <span className="sr-only">{t("discover.filter.searchPlaceholder")}</span>
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
            <IconSearch size={16} />
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("discover.filter.searchPlaceholder")}
            className="input w-full pl-9"
          />
        </label>
      </div>

      {/* Candidate list */}
      {targetsQuery.isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : !data || data.candidates.rows.length === 0 ? (
        <EmptyState
          icon={<IconUsers size={26} />}
          title={t("discover.empty.title")}
          description={t("discover.empty.subtitle")}
        />
      ) : (
        <>
          <ul className="space-y-2">
            {data.candidates.rows.map((c) => (
              <CandidateRow key={c.id} c={c} />
            ))}
          </ul>

          <div className="flex items-center justify-between gap-3 pt-1">
            <p className="text-xs text-slate-500">
              {t("discover.pagination.summary", { page, pages })}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-ghost px-3 py-1.5 text-sm disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t("discover.pagination.prev")}
              </button>
              <button
                type="button"
                className="btn-ghost px-3 py-1.5 text-sm disabled:opacity-40"
                disabled={page >= pages}
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
              >
                {t("discover.pagination.next")}
              </button>
            </div>
          </div>
        </>
      )}

      <p className="text-xs text-slate-500">{t("discover.attemptsHelp")}</p>
    </div>
  );
}

const STATUS_TONE: Record<CandidateStatus, string> = {
  available: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30",
  cooldown: "bg-amber-500/15 text-amber-300 ring-amber-400/30",
  processed: "bg-violet-500/15 text-violet-300 ring-violet-400/30",
  follower: "bg-sky-500/15 text-sky-300 ring-sky-400/30",
};

function CandidateRow({ c }: { c: Candidate }): JSX.Element {
  const { t } = useTranslation();
  const sourceLabel = useMemo(() => {
    if (c.source === "manual") return t("discover.source.manual");
    return t(`discover.source.${c.source}`, { value: c.sourceValue });
  }, [c.source, c.sourceValue, t]);

  return (
    <li className="card flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar name={c.username} size="sm" variant="instagram" />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-white">
              @{c.username}
            </span>
            {c.isPrivate && (
              <span
                className="inline-flex items-center text-slate-500"
                title={t("discover.private")}
                aria-label={t("discover.private")}
              >
                <IconLock size={12} />
              </span>
            )}
          </div>
          {c.fullName && (
            <p className="truncate text-xs text-slate-400">{c.fullName}</p>
          )}
          <p className="truncate text-[11px] text-slate-500">{sourceLabel}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pl-11 sm:justify-end sm:pl-0">
        {(c.followerCount != null || c.followingCount != null) && (
          <span className="text-[11px] tabular-nums text-slate-400">
            {(c.followerCount ?? 0).toLocaleString()}{" "}
            {t("discover.summary.followers").toLowerCase()} ·{" "}
            {(c.followingCount ?? 0).toLocaleString()}{" "}
            {t("discover.summary.following").toLowerCase()}
          </span>
        )}
        <span className="text-[11px] tabular-nums text-slate-400">
          {t("discover.attempts", { count: c.attempts })}
        </span>
        <span
          className={clsx(
            "rounded-full px-2.5 py-1 text-[11px] font-medium ring-1",
            STATUS_TONE[c.status]
          )}
        >
          {t(`discover.status.${c.status}`)}
        </span>
      </div>
    </li>
  );
}

type Tone = "slate" | "emerald" | "amber" | "violet" | "sky" | "rose";
const TILE_TONE: Record<Tone, string> = {
  slate: "text-slate-200",
  emerald: "text-emerald-300",
  amber: "text-amber-300",
  violet: "text-violet-300",
  sky: "text-sky-300",
  rose: "text-rose-300",
};

function SummaryTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | undefined;
  tone: Tone;
}): JSX.Element {
  return (
    <div className="card p-3 sm:p-4">
      <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
        {label}
      </p>
      <p
        className={clsx(
          "mt-1.5 text-2xl font-semibold tabular-nums sm:text-3xl",
          TILE_TONE[tone]
        )}
      >
        {value == null ? "—" : value.toLocaleString()}
      </p>
    </div>
  );
}
